import React, { KeyboardEvent } from "react";
import {
  Controller,
  FieldError,
  FieldValues,
  useFormContext,
  UseFormSetValue,
} from "react-hook-form";
import {
  IoIosCheckmarkCircle,
  IoIosEye,
  IoIosEyeOff,
  IoIosInformationCircle,
  IoIosWarning,
} from "react-icons/io"; // Import password icons
import InputMask from "react-input-mask";
import { Col, FormGroup, Input, InputGroup, Label } from "reactstrap";
import Tooltip from "src/components/coreui/Tooltip";
import { CONSTANTS } from "src/constants";
import { FORM_CONSTANT } from "src/constants/form.constants";
import { PROFILE } from "src/constants/profile.constants";
import { FormInputProps } from "src/models/form.models";
import { InputType } from "src/models/notes.models";
import handleResponse from "src/services/apiConfig/api.response.service";
import { validateEmail } from "src/services/common.Info.service";

import CIcon from "@coreui/icons-react";
import { CInputGroupText } from "@coreui/react";

const FormInput: React.FC<FormInputProps> = ({
  field,
  fieldDisabled,
  setValue,
}) => {
  const {
    id,
    className,
    labelName,
    disabled = fieldDisabled ?? CONSTANTS.COMMON_CONSTANTS.NO,
    name,
    placeHolder,
    icon,
    infoTooltip,
    inputType,
    notDisplayError,
    type = FORM_CONSTANT.TEXT,
    rules,
    isValidEmail,
    validEmail,
  } = field;
  const {
    control,
    formState: { errors },
    getValues,
    setValue: formSetValue,
  } = useFormContext();
  const value = getValues(name);
  const [showPassword, setShowPassword] = React.useState(false);
  const [isEmailValid, setIsEmailValid] = React.useState<boolean | null>(null);
  const [isEmail, setIsEmail] = React.useState<boolean>(false);
  const emailValidate = (email: string): boolean => {
    const emailRegex = /^\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\s*$/;
    return emailRegex.test(email);
  };
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedValidation = React.useCallback(
    (value: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        const isValid = emailValidate(value);
        setIsEmail(isValid);
        if (isValid && !disabled) {
          const res = await validateEmail(value?.trim());
          const resData = await handleResponse(res);
          if (resData?.ok && resData?.data?.status) {
            setIsEmailValid(true);
            formSetValue("validEmail", true);
          } else {
            formSetValue("validEmail", false);
            setIsEmailValid(false);
          }
        }
      }, 500);
    },
    [isValidEmail, timerRef]
  );

  React.useEffect(() => {
    if (validEmail && isValidEmail) {
      setIsEmail(true);
      setIsEmailValid(validEmail);
      formSetValue("validEmail", true);
    } else if (isValidEmail && value) {
      debouncedValidation(value);
    }
  }, [isValidEmail, value, validEmail]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  function allowType(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === FORM_CONSTANT.BACK_SPACE) {
      return;
    } else if (
      !PROFILE.NUMBER_REGEX.test(event.key) &&
      inputType === FORM_CONSTANT.MOBILE_NUMBER
    ) {
      event.preventDefault();
    } else if (
      !PROFILE.NUMBER_REGEX.test(event.key) &&
      inputType === FORM_CONSTANT.NUMBER
    ) {
      event.preventDefault();
    } else if (
      !/^[0-9.]$/.test(event.key) &&
      inputType === FORM_CONSTANT.DECIMAL
    ) {
      event.preventDefault();
    }
  }
  const isMobile = inputType === FORM_CONSTANT.MOBILE_NUMBER;
  const isCheckbox = type === "checkbox";

  if (isCheckbox) {
    return (
      <FormGroup className={`w-100 dd-form-group ${className ?? ""}`}>
        <div className="d-flex align-items-center">
          {labelName && (
            <Label className="dd-label d-flex align-items-center" for={id}>
              {labelName}
              {infoTooltip && (
                <Tooltip content={infoTooltip} placement="top">
                  <span className="ms-1 d-inline-flex align-items-center">
                    <IoIosInformationCircle className="cursor-hand" size={16} />
                  </span>
                </Tooltip>
              )}
            </Label>
          )}
          <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field }) => (
              <Input
                id={id}
                name={name}
                type="checkbox"
                checked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                disabled={disabled}
                className="form-check-input form-check-border-color form-check-tick-input form-check-color form-check-width position-relative mt-0 shadow-none form-day ms-3"
              />
            )}
          />
        </div>
        {!notDisplayError && errors[name] && (
          <span className="text-danger dd-form-error">
            {errors[name] && (errors[name] as FieldError).message}
          </span>
        )}
      </FormGroup>
    );
  }

  return (
    <InputGroup className={`dd-input ${className} `}>
      <FormGroup className="w-100 dd-form-group">
        <div className="d-flex justify-content-center align-items-center">
          {labelName && (
            <Label
              className={`dd-label ${infoTooltip ? "d-flex align-items-center" : ""}`}
              for={id}
            >
              {labelName}
              {infoTooltip && (
                <Tooltip content={infoTooltip} placement="top">
                  <span className="ms-1 d-inline-flex align-items-center">
                    <IoIosInformationCircle className="cursor-hand" size={16} />
                  </span>
                </Tooltip>
              )}
            </Label>
          )}
          <Col>
            <Controller
              control={control}
              name={name}
              rules={rules}
              render={({ field }) => (
                <div className="d-flex dd-input-border position-relative">
                  {icon && (
                    <CInputGroupText>
                      <CIcon icon={icon} />
                    </CInputGroupText>
                  )}
                  {isMobile ? (
                    <InputMask
                      mask={CONSTANTS.MOBILE_PATTERN}
                      value={field.value}
                      disabled={disabled}
                      onChange={e => {
                        const input = e.target;
                        const selectionStart = input.selectionStart ?? 0;
                        const rawValue = e.target.value.replace(
                          /\D/g,
                          CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE
                        );
                        (setValue as UseFormSetValue<FieldValues>)?.(
                          field.name,
                          rawValue
                        );
                        field.onChange(rawValue);
                        requestAnimationFrame(() => {
                          input.setSelectionRange(
                            selectionStart,
                            selectionStart
                          );
                        });
                      }}
                      onBlur={field.onBlur}
                    >
                      {inputProps => (
                        <Input
                          {...inputProps}
                          id={id}
                          name={name}
                          type="text"
                          placeholder={placeHolder}
                          autoComplete="off"
                          onKeyDown={allowType}
                          min="0"
                          className={disabled ? "dd-disabled-input" : ""}
                        />
                      )}
                    </InputMask>
                  ) : (
                    <Input
                      id={id}
                      name={name}
                      type={
                        type === "password" && showPassword
                          ? "text"
                          : (type as InputType)
                      }
                      value={field.value}
                      onChange={e => {
                        field.onChange(e);
                        isValidEmail && debouncedValidation(e.target.value);
                      }}
                      onBlur={field.onBlur}
                      placeholder={placeHolder}
                      disabled={disabled}
                      autoComplete="off"
                      onKeyDown={allowType}
                      min={"0"}
                      className={disabled ? "dd-disabled-input" : ""}
                    />
                  )}
                  {type === "password" &&
                    (showPassword ? (
                      <IoIosEyeOff
                        className="dd-password-icon"
                        onClick={togglePasswordVisibility}
                      />
                    ) : (
                      <IoIosEye
                        className="dd-password-icon"
                        onClick={togglePasswordVisibility}
                      />
                    ))}
                </div>
              )}
            />
          </Col>
          {isEmail && value && (
            <>
              {isEmailValid ? (
                <IoIosCheckmarkCircle className="text-success fs-4 dd-email-validate-icon" />
              ) : (
                <Tooltip content="Invalid Email" placement="right">
                  <span>
                    <IoIosWarning className="text-danger fs-4 dd-email-validate-icon" />
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </div>
        {!notDisplayError && errors[name] && (
          <span className="text-danger dd-form-error">
            {errors[name] && (errors[name] as FieldError).message}
          </span>
        )}
      </FormGroup>
    </InputGroup>
  );
};

export default FormInput;
